import { dropTask, restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import { BufferedChangeset } from 'validated-changeset';
import AduProfile, { ResourceName as AduProfileResourceName } from 'volta/models/adu-profile';
import BufferProfile, { ResourceName as BufferProfileRN } from 'volta/models/buffer-profile';
import { ADU_TYPES } from 'volta/models/constants/adu';
import PlanningMethodologies from 'volta/models/constants/planning-methodologies';
import DafProfileModel from 'volta/models/daf-profile';
import PlanningSetting, { DAF_FIELDS } from 'volta/models/planning-setting';
import SimulationItemSetting, {
  Commands as SimulationItemSettingCommands,
  Events as SimulationItemSettingEvents
} from 'volta/models/simulation-item-setting';
import SkuModel from 'volta/models/sku';
import {
  IPlanningSettingData,
  TPlanningMethodology
} from 'volta/models/types/planning-settings';
import { IDefaultBufferSettings } from 'volta/models/types/settings';
import {
  IStalloneCalculationResult,
  IStalloneValues
} from 'volta/models/types/simulation';
import StoreService from 'volta/services/-store';
import AppSettings from 'volta/services/app-settings';
import AuthorizationService from 'volta/services/authorization';
import BvFlashService from 'volta/services/bv-flash';
import EventStreamService from 'volta/services/event-stream';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { upper } from 'volta/utils/text-utils';

import { action } from '@ember/object';
import { once } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { classify } from '@ember/string';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { IAduEditorSubmit } from '../adu-editor';
import { IBufferSettingsSubmit } from '../buffer-profile-editor';
import { IDafEditorSubmit } from '../daf-editor';
import { IMrpSettingsSubmit } from '../mrp-settings-editor';
import { IOrderCycleSubmit } from '../order-cycle-editor';
import { IOrderSpikeSubmit } from '../order-spike-editor';

type TSettingsChangeset = BufferedChangeset & PlanningSetting;

export interface IEditorDefinition {
  enabled: boolean;
  methodology?: TPlanningMethodology;
  fields: Array<keyof IPlanningSettingData>;
}

export type TEditorKey =
  | 'adu'
  | 'bufferProfile'
  | 'daf'
  | 'methodology'
  | 'mrp'
  | 'orderCycle'
  | 'orderSpike'
  | 'orderSpikeLt'
  | 'unmanaged';

export type TEditors = Record<TEditorKey, IEditorDefinition>;

export const EditorDefinitions: TEditors = {
  orderCycle: {
    enabled: false,
    methodology: 'BUFFER',
    fields: ['orderCycle']
  },
  methodology: {
    enabled: false,
    fields: ['methodology']
  },
  bufferProfile: {
    enabled: false,
    methodology: 'BUFFER',
    fields: [
      'bufferProfileId',
      'varFactor',
      'leadTimeFactor',
      'fixedRedBase',
      'fixedRedSafety',
      'fixedYellow',
      'fixedGreen'
    ]
  },
  adu: {
    enabled: false,
    methodology: 'BUFFER',
    fields: [
      'aduProfileId',
      'aduType',
      'aduHorizon',
      'aduHorizonOffset',
      'aduOverride',
      'aduMin',
      'discontinuedDemand'
    ]
  },
  daf: {
    enabled: false,
    methodology: 'BUFFER',
    fields: ['dafProfileId', 'dafWeekly', 'dafMonthly']
  },
  orderSpike: {
    enabled: false,
    methodology: 'BUFFER',
    fields: [
      'orderSpikeHorizon',
      'orderSpikeThreshold',
      'orderSpikeLtHorizon',
      'orderSpikeLtThreshold'
    ]
  },
  orderSpikeLt: {
    enabled: false,
    methodology: 'BUFFER',
    fields: ['orderSpikeLtHorizon', 'orderSpikeLtThreshold']
  },
  mrp: {
    enabled: false,
    methodology: 'MRP',
    fields: ['demandHorizon', 'isProtected', 'reorderStrategy']
  },
  unmanaged: {
    enabled: false,
    methodology: 'NONE',
    fields: ['isProtected']
  }
};

interface IArgs {
  changeset: TSettingsChangeset;
  aduChartOptions?: object;
  sku?: SkuModel;
  itemSetting?: SimulationItemSetting;
  showDiscontinued?: boolean;
  disabled?: boolean;
  settingsSuggestionNormalMode?: boolean;
  onUpdate?: (editorName: TEditorKey, cs: TSettingsChangeset) => void;
}

export default class BufferSettingsUpdater extends Component<IArgs> {
  // Services
  // ~~~~~
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;
  @service eventStream!: EventStreamService;
  @service authorization!: AuthorizationService;
  @service appSettings!: AppSettings;

  // Properties
  // ~~~~~

  @tracked bufferProfiles: BufferProfile[] = [];
  @tracked aduProfiles: AduProfile[] = [];
  @tracked dafProfiles: DafProfileModel[] = [];
  @tracked stalloneData: IStalloneValues = {
    ...(this.args.itemSetting?.stallone ?? {}),
    id: '',
    status: 'NOT_CALCULATED'
  };
  @tracked showSettingsSuggestionModal = false;
  @tracked settingsSuggestionNormalMode: boolean = this.args.settingsSuggestionNormalMode ?? true;

  aduTypes = ADU_TYPES;

  // Getters & CPs
  // ~~~~~

  get methodologies() {
    return Object.keys(PlanningMethodologies).map((key: TPlanningMethodology) => {
      return { key, label: PlanningMethodologies[key] };
    });
  }

  get defaultBufferProfile() {
    const profiles = this.args.sku
      ? this.bufferProfiles.filter((bp) => {
        return (
          bp.isDefault &&
          upper(this.args.sku?.varCategory as string ?? 'medium') === bp.varCategory &&
          upper(this.args.sku?.leadTimeCategory as string ?? 'medium') === bp.leadTimeCategory &&
          bp.acqCode === this.args.sku?.acqCode
        );
      })
      : [];

    if (profiles.length > 0) {
      return profiles[0];
    }

    return undefined;
  }

  get currentBufferProfile() {
    return this.bufferProfiles.find(
      (bp) => bp.bufferProfileId === this.args.changeset.get('settings.bufferProfileId')
    );
  }

  get currentAduDetails() {
    const { aduHorizon, aduHorizonOffset, varFactor, leadTimeFactor } =
      this.args.changeset.settings;
    return {
      aduHorizon,
      aduHorizonOffset,
      varFactor,
      leadTimeFactor,
      bufferProfileCode: this.currentBufferProfile?.code
    };
  }

  get defaultAduDetails() {
    if (!this.args.sku) {
      return;
    }
    const acqCode = classify(this.args.sku.acqCode.toLowerCase());
    const defaultBufferSettings = this.args.sku.config?.settings.defaultBuffer!;
    const aduHorizon =
      defaultBufferSettings?.[`aduHorizon${acqCode}` as keyof IDefaultBufferSettings];
    const aduHorizonOffset =
      defaultBufferSettings?.[`aduHorizonOffset${acqCode}` as keyof IDefaultBufferSettings] ?? 1.0;
    return {
      aduType: 'ROLLING',
      aduHorizon,
      aduHorizonOffset: Math.ceil(aduHorizon * aduHorizonOffset),
      aduOverride: null,
      bufferProfileId: this.defaultBufferProfile?.bufferProfileId,
      bufferProfileCode: this.defaultBufferProfile?.code,
      varFactor: this.defaultBufferProfile?.varFactor,
      leadTimeFactor: this.defaultBufferProfile?.leadTimeFactor,
      aduProfileId: null
    };
  }

  get stalloneEnabled() {
    return (
      this.authorization.can(SimulationItemSettingCommands.RunStallone, 'simulationScenarios') &&
      this.appSettings.features?.simulation.stallone
    );
  }

  get stalloneStatus() {
    return this.stalloneData.status;
  }

  get stalloneIsNotCalculated() {
    return this.stalloneStatus === 'NOT_CALCULATED';
  }

  get stalloneIsCalculating() {
    return this.stalloneStatus === 'CALCULATING';
  }

  get stalloneIsCalculated() {
    return this.stalloneStatus === 'CALCULATED';
  }

  get stalloneSuggestion() {
    return this.stalloneData.lastSuggestion;
  }

  get stalloneStats() {
    return this.stalloneData.lastSuggestionStats;
  }

  get currentStats() {
    return this.stalloneData.currentStats;
  }

  // Lifecycle hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      'simulationItemSettings',
      SimulationItemSettingEvents.StalloneCalculationDone,
      this._onStalloneCalculationDone,
      this
    );
  }

  @action
  initBufferSettings() {
    this.fetchAduProfiles.perform();
    this.fetchBufferProfiles.perform();
    this.fetchDafProfiles.perform();

    this.showSettingsSuggestionModal =
      !!this.args.sku && upper(this.args.changeset.change.settings?.methodology) === 'BUFFER';

    // refresh data from the server
    this.refreshStalloneValues();
  }

  willDestroy() {
    super.willDestroy();
    this.eventStream.removeHandlers(this);
  }

  // Actions
  // ~~~~~

  @action
  applyDefaultSettings() {
    if (this.args.sku?.config?.settings.defaultBuffer) {
      this.showSettingsSuggestionModal = false;
      this.args.changeset.set(
        'settings.bufferProfileId',
        this.defaultBufferProfile?.bufferProfileId
      );
      this.args.changeset.set('settings.varFactor', this.defaultBufferProfile?.varFactor);
      this.args.changeset.set('settings.leadTimeFactor', this.defaultBufferProfile?.leadTimeFactor);
      this.args.changeset.set('settings.fixedRedBase', null);
      this.args.changeset.set('settings.fixedRedSafety', null);
      this.args.changeset.set('settings.fixedYellow', null);
      this.args.changeset.set('settings.fixedGreen', null);
      this.args.changeset.set('settings.aduMin', null);
      this.changeAduDetails(this.defaultAduDetails as IAduEditorSubmit);
    } else {
      this.bvFlash.error(this.intl.t('noConfigFound'));
    }
  }

  @action
  applyStallone() {
    const settings = this.stalloneData.lastSuggestion;
    if (!settings) {
      return;
    }

    this.showSettingsSuggestionModal = false;
    this.args.changeset.set('settings.varFactor', settings.varFactor);
    this.args.changeset.set('settings.leadTimeFactor', settings.leadTimeFactor);
    this.args.changeset.set('settings.fixedRedBase', undefined);
    this.args.changeset.set('settings.fixedRedSafety', undefined);
    this.args.changeset.set('settings.fixedYellow', undefined);
    this.args.changeset.set('settings.fixedGreen', undefined);

    this.changeAduDetails(settings);
  }

  @action
  changeMethodology(methodology: TPlanningMethodology) {
    // Settings can either be a ObjectTreeNode (for no explained reason yet) or a "POJO".
    // In case of "POJO", methodology is not set correctly (for no explained reason also)
    // Set Deep fix this issue.
    this.args.changeset.setDeep(
      this.args.changeset,
      'settings.methodology',
      methodology === 'NONE' ? undefined : methodology
    );
    this.args.changeset.validate();
    this.args.onUpdate?.('methodology', this.args.changeset);
  }

  @action
  changeOrderCycle(attrs: IOrderCycleSubmit) {
    this.args.changeset.set('settings.orderCycle', attrs.orderCycle);
    this.args.onUpdate?.('orderCycle', this.args.changeset);
  }

  @action
  changeBufferProfile(attrs: IBufferSettingsSubmit) {
    this.args.changeset.set('settings.bufferProfileId', attrs.bufferProfileId);
    this.args.changeset.set('settings.leadTimeFactor', attrs.leadTimeFactor ?? 1);
    this.args.changeset.set('settings.varFactor', attrs.varFactor ?? 1);
    this.args.changeset.set('settings.fixedRedBase', attrs.fixedRedBase ?? null);
    this.args.changeset.set('settings.fixedRedSafety', attrs.fixedRedSafety ?? null);
    this.args.changeset.set('settings.fixedYellow', attrs.fixedYellow ?? null);
    this.args.changeset.set('settings.fixedGreen', attrs.fixedGreen ?? null);
    this.args.onUpdate?.('bufferProfile', this.args.changeset);
  }

  @action
  changeAduDetails(attrs: IAduEditorSubmit) {
    this.args.changeset.set('settings.aduProfileId', attrs.aduProfileId);

    if (isPresent(attrs.aduType)) {
      this.args.changeset.set('settings.aduType', attrs.aduType);
    }

    // Here we explicitly set the value to null when undefined
    // so that the value is erased on the backend
    this.args.changeset.set(
      'settings.aduOverride',
      isPresent(attrs.aduOverride) ? attrs.aduOverride : null
    );

    this.args.changeset.set('settings.aduMin', attrs.aduMin);

    if (isPresent(attrs.aduHorizon)) {
      const aduHorizon = Math.round(attrs.aduHorizon as number);

      if (!isNaN(aduHorizon)) {
        this.args.changeset.set('settings.aduHorizon', aduHorizon);
      }
    }

    if (isPresent(attrs.aduHorizonOffset)) {
      const aduHorizonOffset = Math.round(attrs.aduHorizonOffset as number);

      if (!isNaN(aduHorizonOffset)) {
        this.args.changeset.set('settings.aduHorizonOffset', aduHorizonOffset);
      }
    }

    if (isPresent(attrs.discontinuedDemand)) {
      this.args.changeset.set('settings.discontinuedDemand', attrs.discontinuedDemand);
    }

    this.args.onUpdate?.('adu', this.args.changeset);
  }

  @action
  changeDAF(attrs: IDafEditorSubmit) {
    const settings = Object.assign({}, this.args.changeset.get('pendingData.settings'));
    DAF_FIELDS.forEach((df) => {
      settings[df] = attrs[df as keyof IDafEditorSubmit];
    });
    this.args.changeset.set('settings', settings);

    this.args.onUpdate?.('daf', this.args.changeset);
  }

  @action
  changeOrderSpike(attrs: IOrderSpikeSubmit) {
    this.args.changeset.set('settings.orderSpikeHorizon', attrs.orderSpikeHorizon);
    this.args.changeset.set('settings.orderSpikeThreshold', attrs.orderSpikeThreshold);
    this.args.changeset.set('settings.orderSpikeHorizonUnit', attrs.orderSpikeHorizonUnit);
    this.args.changeset.set('settings.orderSpikeLtHorizon', attrs.orderSpikeLtHorizon);
    this.args.changeset.set('settings.orderSpikeLtThreshold', attrs.orderSpikeLtThreshold);
    this.args.changeset.set('settings.orderSpikeLtHorizonUnit', attrs.orderSpikeLtHorizonUnit);
    this.args.onUpdate?.('orderSpike', this.args.changeset);
  }

  @action
  changeMRPSettings(attrs: IMrpSettingsSubmit) {
    this.args.changeset.set('settings.demandHorizon', attrs.demandHorizon);
    this.args.changeset.set('settings.isProtected', Boolean(attrs.isProtected));
    this.args.changeset.set('settings.reorderStrategy', attrs.reorderStrategy);
    this.args.onUpdate?.('mrp', this.args.changeset);
  }

  @action
  changeUnmanagedSettings(isProtected: boolean) {
    this.args.changeset.set('settings.isProtected', isProtected);
    this.args.onUpdate?.('unmanaged', this.args.changeset);
  }

  @action
  onSettingsSuggestionModalRender() {
    once(this, () => this.refreshStalloneValues());
  }

  @action
  toggleSettingsSuggestionMode() {
    this.settingsSuggestionNormalMode = !this.settingsSuggestionNormalMode;
  }

  // Helpers
  // ~~~~~~~

  refreshStalloneValues() {
    if (this.stalloneEnabled && this.args.itemSetting) {
      this.fetchStalloneValues.perform();
    }
  }

  // Tasks
  // ~~~~~~

  runStalloneTask= dropTask(async () => {
    const entityId = this.args.itemSetting?.id;
    if (!entityId) {
      return;
    }

    try {
      await SimulationItemSetting.runStallone({ entityId });
      this.stalloneData = { ...(this.stalloneData ?? {}), status: 'CALCULATING' };
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  fetchBufferProfiles= restartableTask(async () => {
    try {
      this.bufferProfiles = (await this.store.query(BufferProfileRN.singular, {
        sort: 'code',
        page: { limit: 500, offset: 0 }
      })).toArray();
      return this.bufferProfiles;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  fetchAduProfiles= restartableTask(async () => {
    try {
      this.aduProfiles = (await this.store.query(AduProfileResourceName.singular, {
        sort: 'code',
        page: { limit: 500, offset: 0 }
      })).toArray();
      return this.aduProfiles;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  fetchDafProfiles= restartableTask(async () => {
    try {
      this.dafProfiles = (await this.store.query('daf-profile', {
        sort: 'name',
        page: { limit: 500, offset: 0 }
      })).toArray();
      return this.dafProfiles;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  fetchStalloneValues= dropTask(async () => {
    if (!this.args.itemSetting) {
      return;
    }
    try {
      this.stalloneData = await this.store.customQuery(
        'simulation-item-setting',
        'stalloneValues',
        this.args.itemSetting?.id,
        undefined
      );
      return this.stalloneData;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  // Event Handlers
  // ~~~~~~

  _onStalloneCalculationDone(payload: IStalloneCalculationResult) {
    if (this.args.itemSetting?.id !== payload.id) {
      return;
    }
    this.stalloneData = { ...this.stalloneData, ...payload } as IStalloneValues;
  }
}
